<template>
  <div :class="['ls-inline-edit', { 'bottom-border': bottomBorder }]">
    <div class="label-and-input-wrapper column is-two-thirds">
      <span class="label-text column is-one-third" test-id="ls_inline_edit_label">
        {{ label | titleCase }}
      </span>
      <div class="data-or-input-text">
        <span v-if="!hideInput" test-id="ls_inline_edit-value">{{ value }}</span>
        <span v-else-if="mask" test-id="inline_input">
          <LsInputMasked
            v-model="localValue"
            test-id="ls_inline_edit_masked"
            :track-id="trackId"
            :name="name"
            :mask="mask"
            :loading="loading"
            :placeholder="placeholder"
            :disabled="disabled"
            :validation="validation"
          />
        </span>
        <span v-else test-id="inline-input">
          <LsInput
            v-model="localValue"
            test-id="ls_inline_edit_regular"
            :track-id="trackId"
            :name="name"
            :validation="validation"
            :type="type"
            :loading="loading"
            :placeholder="placeholder"
            :disabled="disabled"
            maxlength="50"
          />
        </span>
      </div>
    </div>

    <div class="icon-wrapper column is-one-third">
      <span
        v-if="!editMode"
        class="pencil-icon clickable"
        :class="{ 'no-click': loading || disabled }"
        @mouseover="pencilColor = hideInput || disabled ? 'gray-secondary' : 'primary'"
        @mouseleave="pencilColor = 'gray-secondary'"
        @click="toggleEditMode"
      >
        <LsIcon type="edit" size="md" :color="pencilColor" test-id="ls_inline_edit_pencil" />
      </span>
      <span v-else class="accept-or-cancel-icons">
        <span
          class="cancel-icon clickable"
          :class="{ 'no-click': loading }"
          @mouseover="cancelColor = 'danger'"
          @mouseleave="cancelColor = 'gray-secondary'"
          @click="toggleEditMode"
        >
          <LsIcon type="cancel" size="md" :color="cancelColor" test-id="ls_inline_input_cancel" />
        </span>
        <span
          class="accept-icon clickable"
          :class="{ 'no-click': loading }"
          @click="onSubmit"
          @mouseover="checkmarkColor = 'primary'"
          @mouseleave="checkmarkColor = 'gray-secondary'"
        >
          <LsIcon
            type="checkmark"
            size="md"
            :color="checkmarkColor"
            test-id="ls_inline_edit_checkmark"
          />
        </span>
      </span>
    </div>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import { lsPropTypeHelpers } from 'ls-vue-common';
import { LsIcon } from '@/components/common';
import LsInput from '@/components/common/LsInput/LsInput.vue';
import LsInputMasked from '@/components/common/LsInputMasked/LsInputMasked.vue';
import _ from 'lodash';

export default {
  name: 'LsInlineEdit',
  components: { LsInput, LsInputMasked, LsIcon },
  props: {
    trackId: VueTypes.custom(lsPropTypeHelpers.trackID).isRequired,
    name: VueTypes.string.isRequired,
    type: VueTypes.string.def('text'),
    label: VueTypes.string,
    value: VueTypes.string,
    placeholder: VueTypes.string,
    mask: VueTypes.string,
    validation: VueTypes.object.def(_.constant(null)),
    loading: VueTypes.bool.def(false),
    disabled: VueTypes.bool.def(false),
    bottomBorder: VueTypes.bool.def(true),
  },
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    return {
      editMode: false,
      pencilColor: 'gray-secondary',
      checkmarkColor: 'gray-secondary',
      cancelColor: 'gray-secondary',
      localValue: this.value,
    };
  },
  computed: {
    hideInput() {
      return this.loading || this.editMode;
    },
  },
  methods: {
    toggleEditMode() {
      if (this.loading || this.disabled) {
        return;
      }
      this.pencilColor = 'gray-secondary';
      this.localValue = this.value;
      this.editMode = !this.editMode;
    },
    async onSubmit() {
      this.$emit('current-field', this.name);
      const valid = await this.$validator.validateAll();
      if (this.loading || !valid) {
        return;
      }
      this.editMode = false;
      this.$emit('edit-success', this.localValue);
    },
  },
};
</script>

<style lang="scss" scoped>
.bottom-border {
  border-bottom: 1px solid $primary-light;
}
.ls-inline-edit {
  display: flex;
  align-items: center;

  .label-and-input-wrapper {
    height: 50px;
    display: flex;
    align-items: center;
    padding: 0;
  }
  .label-text {
    width: 50%;
    font-weight: 700;
  }
  .data-or-input-text {
    display: flex;
    align-items: center;
    height: 30px;
  }
  .icon-wrapper {
    display: flex;
    justify-content: flex-end;
  }

  .accept-icon {
    padding-left: 10px;
  }
  .clickable {
    cursor: pointer;
  }
  .no-click {
    cursor: not-allowed;
  }
}
@media (max-width: $mobile-max-width) {
  .ls-inline-edit {
    .label-and-input-wrapper {
      height: 80px;
      flex-direction: column;
    }
    .label-text,
    .data-or-input-text {
      width: 100%;
    }

    .label-text {
      padding: 10px 0;
    }

    .data-or-input-text {
      padding-bottom: 15px;
    }
  }
}
</style>
